/**
 * Dynamic new-thread STARTER chips (owner directive 2026-09-02).
 *
 * The chips under a new thread's greeting were four hardcoded strings — the
 * same four on every new thread, for every account, forever, one of them with a
 * literal `[Counterparty Name]` in it. They came from `GENERIC_WELCOME_STARTERS`
 * via useTenantChatConfig, and the account's `suggestedAutomations` pool that
 * was meant to personalize this surface only ever fed the greeting TEXT.
 *
 * This hook fills `resolveSuggestionChips`' highest-precedence `personalized`
 * slot with a set generated from the live account picture — memory (including
 * anything the user or organization has TAUGHT about how to suggest), recent
 * thread topics, connected apps, active routines, uploaded documents. The
 * hardcoded list stays as the instant floor underneath, so the row is never
 * blank and never waits; the moment the real set lands it takes over.
 *
 * Cheap by construction: the server caches per viewer under a grounding
 * fingerprint, so opening ten new threads in an afternoon is one generation and
 * nine instant hits — and it regenerates when the picture actually changes
 * rather than on a timer alone.
 */

import React from 'react';
import { fetchThreadFollowupSuggestions } from '../../services/threadRecommendations/followupClient';

/**
 * Cold-miss retries. The server generates synchronously, so the FIRST call
 * usually returns the real set; these cover the fail-open answer
 * (`generating: true`) and a transport blip. Deliberately short — the floor is
 * already on screen, so there is nothing to rush.
 */
const RETRY_DELAYS_MS = [4_000, 12_000, 30_000];

export function useStarterSuggestions(args: {
  threadId: string | null | undefined;
  /** False while the thread has real content, is mid-turn, or chips are off. */
  enabled: boolean;
}): string[] {
  const { threadId, enabled } = args;
  const [byThread, setByThread] = React.useState<Record<string, string[]>>({});

  // Starters are per-VIEWER, not per-thread: the server keys its cache that
  // way, so one resolved set serves every new thread this session. Keyed here
  // by thread only so a resolved set cannot leak into a thread that has since
  // gained content.
  const key = threadId || '';
  const resolved = byThread[key];

  React.useEffect(() => {
    if (!enabled || !key || resolved) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const attempt = async (step: number): Promise<void> => {
      if (cancelled) return;
      const result = await fetchThreadFollowupSuggestions({
        threadId: key,
        tail: [],
        starter: true,
      }).catch(() => null);
      if (cancelled) return;
      const chips = (result?.suggestions || []).filter((s) => s.trim().length > 0);
      if (chips.length > 0) {
        setByThread((prev) => (prev[key] ? prev : { ...prev, [key]: chips }));
        return;
      }
      // Nothing usable yet. Keep the floor visible and try again — never write
      // an empty array, which would pin a blank row for this thread.
      const delay = RETRY_DELAYS_MS[step];
      if (delay === undefined) return;
      timer = setTimeout(() => { void attempt(step + 1); }, delay);
    };

    void attempt(0);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [enabled, key, resolved]);

  return resolved || [];
}
